import { useState } from "react";
import { useMgPsycho, useMgQual, useMgQuant, type BrandImagery, type MgPsycho } from "../lib/realdata";

export default function Dashboards() {
  const quant = useMgQuant();
  const qual = useMgQual();
  const psycho = useMgPsycho();

  if (!quant) return <LoadingFallback />;

  const w1 = quant.meta.waves[0];
  const w2 = quant.meta.waves[1];
  const w1Top = quant.brand_awareness_by_wave.wave1.rows.slice(0, 8);
  const w2Top = quant.brand_awareness_by_wave.wave2.rows.slice(0, 8);
  const w1MetricLabel = niceMetric(quant.brand_awareness_by_wave.wave1.metric);
  const w2MetricLabel = niceMetric(quant.brand_awareness_by_wave.wave2.metric);
  const topBrand = w1Top[0]?.brand ?? "—";
  const topPct = w1Top[0]?.pct ?? 0;

  return (
    <div className="stagger">
      <div className="brand-hero">
        <div>
          <div className="eyebrow">Brand Health · live data</div>
          <h2>Mixer Grinder category · 2 waves, {w1.n_total + w2.n_total} respondents.</h2>
          <div className="h-sub">Wave 1 (n={w1.n_total}) and Wave 2 (n={w2.n_total}). Bajaj leads the aided-awareness funnel.</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn-secondary">Share view</button>
          <button className="btn-primary">Export</button>
        </div>
      </div>

      <div className="filter-strip">
        <span className="f-pill active">Wave <b>1 &amp; 2</b> <span className="x">×</span></span>
        <span className="f-pill active">Brand <b>Top 8</b> <span className="x">×</span></span>
        <span className="f-pill">Audience <b>Owners + Intenders</b></span>
        <span className="f-pill">SEC <b>A, B</b></span>
        <span className="f-pill">Age <b>25-44</b></span>
        <span className="f-pill ghost">+ Add filter</span>
      </div>

      <div className="bh-grid">
        <div className="kpi-strip">
          <div className="kpi-card">
            <div className="lbl">Total respondents</div>
            <div className="val">{(w1.n_total + w2.n_total).toLocaleString()}</div>
            <div className="d">Across 2 waves</div>
          </div>
          <div className="kpi-card">
            <div className="lbl">{w1MetricLabel}</div>
            <div className="val">{topBrand}</div>
            <div className="d up"><b>{topPct}%</b> · Wave 1</div>
          </div>
          <div className="kpi-card">
            <div className="lbl">MG ownership</div>
            <div className="val">{quant.mg_ownership_by_wave.wave1?.pct ?? "—"}%</div>
            <div className="d">Wave 1 · n={quant.mg_ownership_by_wave.wave1?.count}</div>
          </div>
          <div className="kpi-card">
            <div className="lbl">Qual interviews</div>
            <div className="val">{qual?.meta.total_interviews ?? "—"}</div>
            <div className="d">{qual?.meta.cities.join(" · ")}</div>
          </div>
        </div>

        <div className="panel" style={{ gridColumn: "1 / -1" }}>
          <h3>Brand awareness · Wave 1</h3>
          <div className="sub">
            <b>{w1MetricLabel}</b> · top {w1Top.length} brands among {w1.n_total} respondents.
          </div>
          <BrandBars rows={w1Top} total={w1.n_total} highlightBrand="Preethi" />
        </div>

        <div className="panel" style={{ gridColumn: "1 / 2" }}>
          <h3>Brand usage · Wave 2</h3>
          <div className="sub">
            <b>{w2MetricLabel}</b> · the Wave 2 questionnaire replaces "total aware" with "ever used".
            Pulse surfaces both; the harmonisation note is on the Data page.
          </div>
          <BrandBars rows={w2Top} total={w2.n_total} highlightBrand="Preethi" />
        </div>

        <div className="panel">
          <h3>Top pain points · from 18 qual DIs</h3>
          <div className="sub">{qual?.meta.total_interviews ?? 0} depth interviews · {qual?.meta.cities.join(" · ")}</div>
          <ThemeBars themes={qual?.theme_rank.slice(0, 6) ?? []} totalInterviews={qual?.meta.total_interviews ?? 18} />
        </div>

        <div className="panel" style={{ gridColumn: "1 / -1" }}>
          <h3>Theme co-occurrence · 18 qual DIs</h3>
          <div className="sub">
            Pairs of themes mentioned together in the same interview. Diagonal is the theme's own count.
            Darker cell = stronger co-occurrence. This is where causal stories live - when jar cracking is mentioned, what else comes up?
          </div>
          {qual?.cooccurrence ? (
            <CooccurrenceMatrix matrix={qual.cooccurrence.matrix} themes={qual.cooccurrence.themes.slice(0, 10)} totalInterviews={qual.meta.total_interviews} />
          ) : (
            <div className="muted" style={{ padding: 30 }}>Loading co-occurrence…</div>
          )}
        </div>

        <div className="panel" style={{ gridColumn: "1 / -1" }}>
          <h3>Brand imagery · Wave 1</h3>
          <div className="sub">
            Share of respondents (n={w1.n_total}) who associated each brand with each imagery statement
            (QA3 · check-all-that-apply). Statement text was truncated in the SPSS datamap - labels are numeric for now.
          </div>
          {quant.brand_imagery_wave1 ? (
            <ImageryRadar imagery={quant.brand_imagery_wave1} />
          ) : (
            <div className="muted" style={{ padding: 30 }}>Imagery data not loaded.</div>
          )}
        </div>

        <div className="panel" style={{ gridColumn: "1 / -1" }}>
          <h3>Psychographic segmentation · Wave 1</h3>
          <div className="sub">
            PCA on 20 attitudinal statements (T_QA1_1 to T_QA1_20, 1-5 Likert).
            K-means into 5 segments. PC1 captures {psycho ? (psycho.var_explained[0] * 100).toFixed(1) : "…"}%
            of variance, PC2 {psycho ? (psycho.var_explained[1] * 100).toFixed(1) : "…"}%.
          </div>
          {psycho ? (
            <PsychoScatter psycho={psycho} />
          ) : (
            <div className="muted" style={{ padding: 30 }}>Loading PCA…</div>
          )}
        </div>

        <div className="panel" style={{ gridColumn: "1 / -1" }}>
          <h3>Brand awareness × City</h3>
          <div className="sub">
            Top 10 brands across 12 centres · Wave 1 · n={w1.n_total}. Darker cell = higher awareness share within that centre.
            Click a centre to see a pop-out.
          </div>
          {psycho ? <CrosstabHeatmap
            rows={psycho.city_brand.centres.map((c) => ({ id: c, label: `Centre ${c}`, total: psycho.city_brand.totals[c] ?? 0 }))}
            cols={psycho.top_brands}
            cells={psycho.city_brand.rows.map((r) => ({ row: r.centre!, col: r.brand, value: r.pct, count: r.count }))}
          /> : <div className="muted" style={{ padding: 30 }}>Loading crosstab…</div>}
        </div>

        <div className="panel" style={{ gridColumn: "1 / -1" }}>
          <h3>Brand awareness × Psychographic segment</h3>
          <div className="sub">
            Joins the 5 k-means segments with Wave 1 aided brand awareness. Reveals which segments over-index on which brands.
          </div>
          {psycho ? <CrosstabHeatmap
            rows={psycho.segments.map((s) => ({ id: String(s.id), label: s.label, total: s.n }))}
            cols={psycho.top_brands}
            cells={psycho.segment_brand.rows.map((r) => ({ row: String(r.segment), col: r.brand, value: r.pct, count: r.count }))}
            markOutliers
          /> : <div className="muted" style={{ padding: 30 }}>Loading crosstab…</div>}
        </div>
      </div>
    </div>
  );
}

function niceMetric(m: string | null | undefined): string {
  if (m === "total_aware") return "Top aware brand";
  if (m === "ever_used") return "Top ever-used brand";
  if (m === "currently_using") return "Top currently-using brand";
  return "Top brand";
}

function BrandBars({ rows, total, highlightBrand }: { rows: { brand: string; count: number; pct: number }[]; total: number; highlightBrand?: string }) {
  if (!rows.length) return <div className="muted" style={{ padding: 30 }}>No rows.</div>;
  const max = Math.max(...rows.map((r) => r.pct));
  return (
    <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
      {rows.map((r) => {
        const w = (r.pct / max) * 100;
        const isHi = r.brand === highlightBrand;
        return (
          <div key={r.brand} style={{ display: "grid", gridTemplateColumns: "120px 1fr 80px", gap: 12, alignItems: "center" }}>
            <div style={{ fontSize: 13, fontWeight: isHi ? 600 : 500, color: isHi ? "var(--teal-ink)" : "var(--ink)" }}>{r.brand}</div>
            <div style={{ height: 16, background: "var(--page-2)", borderRadius: 3, position: "relative", overflow: "hidden" }}>
              <div style={{
                position: "absolute", left: 0, top: 0, bottom: 0, width: w + "%",
                background: isHi ? "var(--teal)" : "var(--ink-40)",
                borderRadius: 3,
                transition: "width 600ms cubic-bezier(.2,.7,.2,1)",
              }} />
            </div>
            <div style={{ textAlign: "right", fontFamily: "var(--mono)", fontSize: 12.5, fontWeight: 500, color: "var(--ink)" }}>
              {r.pct}% <span className="muted" style={{ fontSize: 11 }}>({r.count})</span>
            </div>
          </div>
        );
      })}
      <div className="muted" style={{ fontSize: 11, marginTop: 4, fontFamily: "var(--mono)" }}>Base n = {total.toLocaleString()}</div>
    </div>
  );
}

function ThemeBars({ themes, totalInterviews }: { themes: { theme: string; respondents_mentioning: number; total_mentions: number }[]; totalInterviews: number }) {
  if (!themes.length) return <div className="muted" style={{ padding: 30 }}>Loading…</div>;
  const max = Math.max(...themes.map((t) => t.respondents_mentioning));
  const palette = ["var(--coral)", "var(--amber)", "var(--teal)", "var(--rose)", "var(--ink-40)", "var(--ink-20)"];
  return (
    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
      {themes.map((t, i) => {
        const w = (t.respondents_mentioning / max) * 100;
        return (
          <div key={t.theme} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, alignItems: "center" }}>
            <div style={{ fontSize: 12.5 }}>
              <div style={{ fontWeight: 500, marginBottom: 4 }}>{t.theme}</div>
              <div style={{ height: 10, background: "var(--page-2)", borderRadius: 3, overflow: "hidden" }}>
                <div style={{ width: w + "%", height: "100%", background: palette[i % palette.length], borderRadius: 3 }} />
              </div>
            </div>
            <div style={{ fontFamily: "var(--mono)", fontSize: 12, fontWeight: 500, color: "var(--ink-2)", whiteSpace: "nowrap", marginTop: 10 }}>
              {t.respondents_mentioning}/{totalInterviews}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ============================================================
   Brand imagery radar — overlays 3 brands on 10 imagery statements.
   Shows % of respondents (from QA3 binary matrix) who associated
   each brand with each statement.
   ============================================================ */
function ImageryRadar({ imagery }: { imagery: BrandImagery }) {
  const available = imagery.brands.slice(0, 10).filter((b) => {
    const row = imagery.matrix[b];
    return row && Object.values(row).some((v) => (v ?? 0) > 2);
  });

  const defaultSelection = ["Preethi", "Bajaj", "Philips"].filter((b) => available.includes(b));
  const [selected, setSelected] = useState<string[]>(
    defaultSelection.length ? defaultSelection : available.slice(0, 3)
  );

  const toggle = (b: string) => {
    setSelected((prev) => {
      if (prev.includes(b)) return prev.filter((x) => x !== b);
      if (prev.length >= 4) return [...prev.slice(1), b];
      return [...prev, b];
    });
  };

  // Compute chart geometry
  const stmts = imagery.statements;
  const size = 440;
  const cx = size / 2;
  const cy = size / 2 - 4;
  const rMax = size / 2 - 52;
  // max of any selected brand's any statement, rounded up to next 10
  let maxVal = 0;
  for (const b of selected) {
    for (const s of stmts) {
      const v = imagery.matrix[b]?.[s.id] ?? 0;
      if (v > maxVal) maxVal = v;
    }
  }
  const axisMax = Math.max(20, Math.ceil(maxVal / 10) * 10);

  const angle = (i: number) => (Math.PI * 2 * i) / stmts.length - Math.PI / 2;

  const point = (valPct: number, i: number) => {
    const r = (valPct / axisMax) * rMax;
    return [cx + r * Math.cos(angle(i)), cy + r * Math.sin(angle(i))];
  };

  const palette = ["var(--teal)", "var(--coral)", "var(--amber)", "var(--rose)"];
  const paletteFill = ["rgba(13,122,110,0.15)", "rgba(226,113,90,0.15)", "rgba(199,147,59,0.15)", "rgba(184,90,122,0.15)"];

  const polygonFor = (brand: string) =>
    stmts.map((s, i) => point(imagery.matrix[brand]?.[s.id] ?? 0, i).join(",")).join(" ");

  const gridSteps = 4;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 20, marginTop: 14 }}>
      <svg viewBox={`0 0 ${size} ${size}`} width="100%" style={{ maxWidth: 520 }}>
        {/* concentric polygons */}
        {Array.from({ length: gridSteps }).map((_, s) => {
          const step = (s + 1) / gridSteps;
          const pts = stmts.map((_st, i) => {
            const [x, y] = [cx + rMax * step * Math.cos(angle(i)), cy + rMax * step * Math.sin(angle(i))];
            return `${x},${y}`;
          }).join(" ");
          return <polygon key={s} points={pts} fill="none" stroke="var(--line-2)" strokeWidth={s === gridSteps - 1 ? 1 : 0.5} />;
        })}
        {/* spokes + axis labels */}
        {stmts.map((s, i) => {
          const [x2, y2] = [cx + rMax * Math.cos(angle(i)), cy + rMax * Math.sin(angle(i))];
          const [lx, ly] = [cx + (rMax + 22) * Math.cos(angle(i)), cy + (rMax + 22) * Math.sin(angle(i))];
          return (
            <g key={s.id}>
              <line x1={cx} y1={cy} x2={x2} y2={y2} stroke="var(--line-2)" strokeWidth={0.5} />
              <text x={lx} y={ly} fontSize="11" fill="var(--ink-60)" textAnchor="middle" dominantBaseline="middle" fontFamily="JetBrains Mono">
                Stmt {s.id}
              </text>
            </g>
          );
        })}
        {/* axis scale hint at top */}
        <text x={cx} y={cy - rMax - 2} fontSize="9" fill="var(--ink-40)" textAnchor="middle" fontFamily="JetBrains Mono">
          {axisMax}%
        </text>
        {/* brand polygons */}
        {selected.map((b, idx) => (
          <g key={b}>
            <polygon
              points={polygonFor(b)}
              fill={paletteFill[idx]}
              stroke={palette[idx]}
              strokeWidth={2}
            />
            {stmts.map((s, i) => {
              const [px, py] = point(imagery.matrix[b]?.[s.id] ?? 0, i);
              return <circle key={s.id} cx={px} cy={py} r={2.5} fill={palette[idx]} />;
            })}
          </g>
        ))}
      </svg>

      {/* Brand selector */}
      <div>
        <div className="eyebrow" style={{ marginBottom: 10 }}>Overlay brands (pick up to 4)</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 320, overflowY: "auto" }}>
          {available.map((b, _idx) => {
            const isActive = selected.includes(b);
            const paletteIdx = selected.indexOf(b);
            return (
              <button
                key={b}
                onClick={() => toggle(b)}
                style={{
                  display: "grid",
                  gridTemplateColumns: "14px 1fr auto",
                  gap: 10, alignItems: "center",
                  padding: "8px 12px",
                  border: isActive ? `1px solid ${paletteIdx >= 0 ? palette[paletteIdx] : "var(--teal)"}` : "1px solid var(--line)",
                  background: isActive ? "var(--card-2)" : "var(--card)",
                  borderRadius: "var(--r-2)",
                  cursor: "pointer",
                  fontSize: 13,
                  textAlign: "left",
                  fontWeight: isActive ? 600 : 500,
                  color: "var(--ink-2)",
                }}
              >
                <span style={{
                  width: 10, height: 10, borderRadius: 2,
                  background: isActive ? palette[paletteIdx] : "transparent",
                  border: `2px solid ${isActive && paletteIdx >= 0 ? palette[paletteIdx] : "var(--line)"}`,
                }} />
                <span>{b}</span>
                {/* Best stmt per brand as a data preview */}
                <BrandTopStatement imagery={imagery} brand={b} />
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: 11.5, color: "var(--ink-60)", marginTop: 10, lineHeight: 1.4 }}>
          Each axis is an imagery statement; the polygon farther from centre means more respondents associated the brand with that statement. Scale maxes at <b className="num">{axisMax}%</b>.
        </div>
      </div>
    </div>
  );
}

function BrandTopStatement({ imagery, brand }: { imagery: BrandImagery; brand: string }) {
  const row = imagery.matrix[brand] ?? {};
  let best: { id: string; v: number } = { id: "", v: -1 };
  for (const s of imagery.statements) {
    const v = row[s.id] ?? 0;
    if (v > best.v) best = { id: s.id, v };
  }
  return (
    <span className="num" style={{ fontSize: 11, color: "var(--ink-60)" }}>
      top S{best.id} {best.v.toFixed(0)}%
    </span>
  );
}

/* ============================================================
   Crosstab heatmap — generic row × col × value grid.
   Colours cells by value (% share) on a teal gradient.
   When markOutliers is set, flags cells whose z-score (within column)
   exceeds ±1.0 — these are the "over-" and "under-indexing" cells.
   ============================================================ */
type CrosstabRow = { id: string; label: string; total: number };
type CrosstabCellData = { row: string; col: string; value: number; count: number };

function CrosstabHeatmap({
  rows, cols, cells, markOutliers = false,
}: {
  rows: CrosstabRow[];
  cols: string[];
  cells: CrosstabCellData[];
  markOutliers?: boolean;
}) {
  // Index cells by row_id, col_name
  const idx = new Map<string, CrosstabCellData>();
  for (const c of cells) idx.set(`${c.row}||${c.col}`, c);

  // Colour scale: map pct to opacity 0..1 on teal
  const maxVal = Math.max(...cells.map((c) => c.value), 1);

  // Column z-scores for outlier marking
  const colStats = new Map<string, { mean: number; std: number }>();
  if (markOutliers) {
    for (const col of cols) {
      const vals = rows.map((r) => idx.get(`${r.id}||${col}`)?.value ?? 0);
      const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
      const std = Math.sqrt(vals.reduce((a, b) => a + (b - mean) ** 2, 0) / vals.length) || 1;
      colStats.set(col, { mean, std });
    }
  }

  return (
    <div style={{ marginTop: 14, overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", fontSize: 12, fontFamily: "var(--sans)" }}>
        <thead>
          <tr>
            <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 500, color: "var(--ink-60)", fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", borderBottom: "1px solid var(--line)" }}>
              {rows.length > 0 && (rows[0].id.match(/^\d+$/) ? "Centre / Segment" : "Row")}
            </th>
            {cols.map((c) => (
              <th key={c} style={{
                padding: "6px 8px", fontWeight: 500, color: "var(--ink)", fontSize: 11.5,
                borderBottom: "1px solid var(--line)", minWidth: 58,
                writingMode: "vertical-rl" as const, transform: "rotate(180deg)", whiteSpace: "nowrap",
                height: 84,
              }}>
                {c}
              </th>
            ))}
            <th style={{ padding: "6px 10px", fontWeight: 500, color: "var(--ink-60)", fontSize: 11, borderBottom: "1px solid var(--line)", whiteSpace: "nowrap" }}>
              base n
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td style={{ padding: "6px 10px", color: "var(--ink)", fontWeight: 500, whiteSpace: "nowrap", borderBottom: "1px solid var(--line-2)" }}>
                {r.label}
              </td>
              {cols.map((col) => {
                const cell = idx.get(`${r.id}||${col}`);
                const v = cell?.value ?? 0;
                const intensity = Math.min(1, v / maxVal);
                // Teal base with alpha for intensity
                const bg = `rgba(13, 122, 110, ${0.08 + intensity * 0.72})`;
                const fg = intensity > 0.55 ? "white" : "var(--ink)";
                let zFlag: null | "over" | "under" = null;
                if (markOutliers) {
                  const st = colStats.get(col);
                  if (st) {
                    const z = (v - st.mean) / st.std;
                    if (z > 1.0) zFlag = "over";
                    else if (z < -1.0) zFlag = "under";
                  }
                }
                return (
                  <td key={col} title={`${r.label} · ${col}: ${v}% (n=${cell?.count ?? 0})`}
                      style={{
                        padding: 0, width: 58, height: 38, textAlign: "center",
                        background: bg, color: fg,
                        fontFamily: "var(--mono)", fontSize: 11.5, fontWeight: 500,
                        borderBottom: "1px solid var(--line-2)",
                        position: "relative",
                      }}>
                    {v > 0 ? v.toFixed(0) : "—"}
                    {zFlag === "over" && (
                      <span style={{
                        position: "absolute", top: 2, right: 4,
                        fontSize: 8, color: "var(--ok)", fontWeight: 700, fontFamily: "var(--mono)",
                      }}>▲</span>
                    )}
                    {zFlag === "under" && (
                      <span style={{
                        position: "absolute", top: 2, right: 4,
                        fontSize: 8, color: "var(--coral-ink)", fontWeight: 700, fontFamily: "var(--mono)",
                      }}>▼</span>
                    )}
                  </td>
                );
              })}
              <td style={{ padding: "6px 10px", color: "var(--ink-60)", fontFamily: "var(--mono)", fontSize: 11, textAlign: "right", borderBottom: "1px solid var(--line-2)" }}>
                {r.total}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: "flex", gap: 14, alignItems: "center", marginTop: 10, fontSize: 11, color: "var(--ink-60)" }}>
        <span>Share of respondents aware (%)</span>
        <div style={{ display: "inline-flex", gap: 2 }}>
          {[0.1, 0.3, 0.5, 0.7, 0.9].map((t) => (
            <div key={t} style={{ width: 24, height: 12, background: `rgba(13,122,110,${0.08 + t * 0.72})` }} />
          ))}
        </div>
        <span className="num" style={{ fontSize: 10 }}>0 → {maxVal.toFixed(0)}%</span>
        {markOutliers && (
          <>
            <span style={{ color: "var(--ok)" }}>▲ over-indexing (z &gt; 1)</span>
            <span style={{ color: "var(--coral-ink)" }}>▼ under-indexing (z &lt; -1)</span>
          </>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   Theme co-occurrence matrix (symmetric)
   ============================================================ */
function CooccurrenceMatrix({
  matrix, themes, totalInterviews,
}: {
  matrix: Record<string, Record<string, number>>;
  themes: string[];
  totalInterviews: number;
}) {
  // Max off-diagonal value sets the heat scale
  let maxOff = 0;
  for (const a of themes) {
    for (const b of themes) {
      if (a !== b) {
        const v = matrix[a]?.[b] ?? 0;
        if (v > maxOff) maxOff = v;
      }
    }
  }
  if (maxOff < 1) maxOff = 1;

  const short = (t: string) => {
    // Compact row labels for readability
    const parts = t.split(" / ");
    return parts[0].replace(/\s*[/(-].*/, "").trim();
  };

  return (
    <div style={{ marginTop: 14, overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", fontFamily: "var(--sans)" }}>
        <thead>
          <tr>
            <th style={{ padding: "6px 8px", borderBottom: "1px solid var(--line)", width: 180 }} />
            {themes.map((t) => (
              <th key={t} title={t} style={{
                padding: "6px 4px", fontSize: 10.5, fontWeight: 500, color: "var(--ink)",
                borderBottom: "1px solid var(--line)", height: 130,
                writingMode: "vertical-rl" as const, transform: "rotate(180deg)", whiteSpace: "nowrap",
              }}>
                {short(t)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {themes.map((a) => (
            <tr key={a}>
              <td title={a} style={{
                padding: "4px 10px", fontSize: 12, fontWeight: 500, color: "var(--ink)",
                borderBottom: "1px solid var(--line-2)", whiteSpace: "nowrap", textAlign: "right",
                maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis",
              }}>
                {a}
              </td>
              {themes.map((b) => {
                const v = matrix[a]?.[b] ?? 0;
                const isDiag = a === b;
                const intensity = isDiag ? v / totalInterviews : v / maxOff;
                const clamped = Math.max(0, Math.min(1, intensity));
                const bg = isDiag
                  ? `rgba(199, 147, 59, ${0.15 + clamped * 0.7})`   // amber for diagonal
                  : v > 0
                    ? `rgba(13, 122, 110, ${0.08 + clamped * 0.72})`  // teal for off-diagonal
                    : "var(--page-2)";
                const fg = clamped > 0.55 ? "white" : "var(--ink-2)";
                return (
                  <td key={b} title={`${a} × ${b}: ${v} interviews`}
                      style={{
                        padding: 0, width: 44, height: 34, textAlign: "center",
                        background: bg, color: fg,
                        fontFamily: "var(--mono)", fontSize: 11.5, fontWeight: 500,
                        borderBottom: "1px solid var(--line-2)",
                      }}>
                    {v > 0 ? v : ""}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: "flex", gap: 14, alignItems: "center", marginTop: 10, fontSize: 11, color: "var(--ink-60)" }}>
        <span>Off-diagonal: interviews mentioning both themes (max {maxOff} of {totalInterviews})</span>
        <span style={{ color: "var(--amber)" }}>Diagonal: theme's own count</span>
      </div>
    </div>
  );
}

/* ============================================================
   Psychographic PCA scatter
   - Each dot is a respondent projected onto PC1 × PC2
   - Colour = k-means segment
   - Hover segment name in right-hand legend to highlight
   ============================================================ */
function PsychoScatter({ psycho }: { psycho: MgPsycho }) {
  const [hoveredSegment, setHoveredSegment] = useState<number | null>(null);

  const size = 460;
  const pad = 44;
  const innerW = size - pad * 2;
  const innerH = size - pad * 2;

  // Bounds
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const p of psycho.points) {
    if (p.pc1 < minX) minX = p.pc1;
    if (p.pc1 > maxX) maxX = p.pc1;
    if (p.pc2 < minY) minY = p.pc2;
    if (p.pc2 > maxY) maxY = p.pc2;
  }
  const padBounds = 0.5;
  minX -= padBounds; maxX += padBounds; minY -= padBounds; maxY += padBounds;
  const xScale = (v: number) => pad + ((v - minX) / (maxX - minX)) * innerW;
  const yScale = (v: number) => pad + innerH - ((v - minY) / (maxY - minY)) * innerH;

  // Zero axes
  const x0 = xScale(0);
  const y0 = yScale(0);

  const palette = ["var(--teal)", "var(--coral)", "var(--amber)", "var(--rose)", "#6B72C4"];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 20, marginTop: 14 }}>
      <svg viewBox={`0 0 ${size} ${size}`} width="100%" style={{ maxWidth: 540 }}>
        {/* grid */}
        <rect x={pad} y={pad} width={innerW} height={innerH} fill="var(--card-2)" stroke="var(--line)" />
        {/* axis lines at zero */}
        {x0 >= pad && x0 <= size - pad && (
          <line x1={x0} y1={pad} x2={x0} y2={size - pad} stroke="var(--line)" strokeDasharray="3 3" />
        )}
        {y0 >= pad && y0 <= size - pad && (
          <line x1={pad} y1={y0} x2={size - pad} y2={y0} stroke="var(--line)" strokeDasharray="3 3" />
        )}
        {/* axis labels */}
        <text x={size - pad} y={size - 18} fontSize="11" textAnchor="end" fill="var(--ink-60)" fontFamily="JetBrains Mono">
          PC1 → status / expressiveness ({(psycho.var_explained[0] * 100).toFixed(1)}%)
        </text>
        <text x={pad} y={pad - 10} fontSize="11" fill="var(--ink-60)" fontFamily="JetBrains Mono" style={{ writingMode: undefined }}>
          PC2 ↑ family / stability ({(psycho.var_explained[1] * 100).toFixed(1)}%)
        </text>

        {/* points */}
        {psycho.points.map((p, i) => {
          const dimmed = hoveredSegment !== null && p.segment !== hoveredSegment;
          return (
            <circle
              key={i}
              cx={xScale(p.pc1)}
              cy={yScale(p.pc2)}
              r={3.5}
              fill={palette[p.segment % palette.length]}
              opacity={dimmed ? 0.1 : 0.55}
              style={{ transition: "opacity 160ms" }}
            />
          );
        })}

        {/* segment centroids with labels */}
        {psycho.segments.map((s) => {
          const [cx_, cy_] = [xScale(s.centroid[0]), yScale(s.centroid[1])];
          const isHovered = hoveredSegment === s.id;
          return (
            <g key={s.id}>
              <circle cx={cx_} cy={cy_} r={isHovered ? 10 : 7} fill="white" stroke={palette[s.id % palette.length]} strokeWidth={2.5} style={{ transition: "r 160ms" }} />
              <text x={cx_} y={cy_ + 4} textAnchor="middle" fontSize="10" fontWeight="600" fill={palette[s.id % palette.length]} fontFamily="JetBrains Mono">
                {s.id + 1}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Segments legend */}
      <div>
        <div className="eyebrow" style={{ marginBottom: 10 }}>Segments (n={psycho.n})</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {psycho.segments.map((s) => {
            const isHovered = hoveredSegment === s.id;
            return (
              <div
                key={s.id}
                onMouseEnter={() => setHoveredSegment(s.id)}
                onMouseLeave={() => setHoveredSegment(null)}
                style={{
                  padding: "10px 12px",
                  border: `1px solid ${isHovered ? palette[s.id % palette.length] : "var(--line)"}`,
                  background: isHovered ? "var(--card-2)" : "var(--card)",
                  borderRadius: "var(--r-2)",
                  cursor: "default",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{
                    width: 18, height: 18, borderRadius: 4,
                    background: palette[s.id % palette.length],
                    color: "white",
                    fontSize: 11, fontWeight: 600,
                    display: "grid", placeItems: "center",
                    fontFamily: "var(--mono)",
                  }}>{s.id + 1}</span>
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink)" }}>{s.label}</span>
                  <span className="num" style={{ fontSize: 11.5, color: "var(--ink-60)", marginLeft: "auto" }}>
                    {s.pct}% · n={s.n}
                  </span>
                </div>
                <div style={{ fontSize: 11.5, color: "var(--ink-60)", lineHeight: 1.45 }}>
                  <span style={{ color: "var(--ok)" }}>▲</span>{" "}
                  {s.top_statements_pos[0]?.label.slice(0, 80)}{s.top_statements_pos[0]?.label.length > 80 ? "…" : ""}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function LoadingFallback() {
  return (
    <div className="stagger">
      <div className="brand-hero">
        <div>
          <div className="eyebrow">Brand Health</div>
          <h2>Loading live data…</h2>
          <div className="h-sub">Reading MG Wave 1 + Wave 2 from /data/mg_quant.json.</div>
        </div>
      </div>
    </div>
  );
}
